import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BLANK = { category: "Languages", name: "", proficiency: 80, years: 1, order: 0 };

export default function AdminSkills() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(BLANK);

  const load = () => api.get("/skills").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const createOne = async () => {
    try {
      await api.post("/skills", draft);
      setDraft(BLANK);
      setCreating(false);
      toast.success("Skill added.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateField = async (id, key, value) => {
    try {
      await api.put(`/skills/${id}`, { [key]: value });
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, [key]: value } : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/skills/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <header className="mb-8 flex items-baseline justify-between">
        <div>
          <p className="label-xs">Skills</p>
          <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Tech stack</h1>
        </div>
        <Button onClick={() => setCreating((v) => !v)} data-testid="skill-add-toggle">
          <Plus className="h-4 w-4" /> Add skill
        </Button>
      </header>

      {creating && (
        <div className="mb-6 border border-border rounded-sm p-4 grid grid-cols-12 gap-3">
          <Input className="col-span-3" placeholder="Category" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
          <Input className="col-span-3" placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="skill-new-name" />
          <Input className="col-span-2" type="number" placeholder="Proficiency" value={draft.proficiency} onChange={(e) => setDraft({ ...draft, proficiency: Number(e.target.value) })} />
          <Input className="col-span-1" type="number" placeholder="Years" value={draft.years ?? ""} onChange={(e) => setDraft({ ...draft, years: e.target.value ? Number(e.target.value) : null })} />
          <Input className="col-span-1" type="number" placeholder="Order" value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} />
          <Button className="col-span-2" onClick={createOne} data-testid="skill-new-save"><Save className="h-4 w-4" /> Save</Button>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left label-xs">
              <th className="p-3">Category</th>
              <th className="p-3">Name</th>
              <th className="p-3 w-32">Proficiency</th>
              <th className="p-3 w-24">Years</th>
              <th className="p-3 w-20">Order</th>
              <th className="p-3 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`skill-row-${s.id}`}>
                <td className="p-2"><Input value={s.category} onBlur={(e) => updateField(s.id, "category", e.target.value)} onChange={(e) => setItems((a) => a.map((x) => x.id === s.id ? { ...x, category: e.target.value } : x))} /></td>
                <td className="p-2"><Input value={s.name} onBlur={(e) => updateField(s.id, "name", e.target.value)} onChange={(e) => setItems((a) => a.map((x) => x.id === s.id ? { ...x, name: e.target.value } : x))} /></td>
                <td className="p-2"><Input type="number" value={s.proficiency} onBlur={(e) => updateField(s.id, "proficiency", Number(e.target.value))} onChange={(e) => setItems((a) => a.map((x) => x.id === s.id ? { ...x, proficiency: Number(e.target.value) } : x))} /></td>
                <td className="p-2"><Input type="number" value={s.years ?? ""} onBlur={(e) => updateField(s.id, "years", e.target.value ? Number(e.target.value) : null)} onChange={(e) => setItems((a) => a.map((x) => x.id === s.id ? { ...x, years: e.target.value ? Number(e.target.value) : null } : x))} /></td>
                <td className="p-2"><Input type="number" value={s.order} onBlur={(e) => updateField(s.id, "order", Number(e.target.value))} onChange={(e) => setItems((a) => a.map((x) => x.id === s.id ? { ...x, order: Number(e.target.value) } : x))} /></td>
                <td className="p-2 text-right"><Button variant="ghost" size="icon" onClick={() => remove(s.id)}><Trash2 className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Changes save on blur.</p>
    </div>
  );
}
